import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Plus, ArrowLeft, Trash2, RefreshCw, X, UploadCloud, Image as ImageIcon } from 'lucide-react';
import {
  fetchWebsiteUploads,
  createWebsiteUpload,
  deleteWebsiteUpload,
  IMG_BASE_URL,
} from './hooks/hooks';

const NAV = '#001f3f';

const CATEGORIES = [
  { value: 'gallery', label: 'Gallery' },
  { value: 'hero', label: 'Hero / Banner' },
  { value: 'project', label: 'Project photo' },
  { value: 'general', label: 'General' },
];

const validationSchema = Yup.object({
  image: Yup.mixed().required('Image is required'),
});

const Toast = ({ toast, onClose }) => {
  if (!toast.open) return null;
  const cls = toast.severity === 'success'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`${cls} border rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 max-w-sm`}>
        <span className="text-sm font-medium">{toast.message}</span>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

function WebsiteUploads() {
  const queryClient = useQueryClient();
  const { data: uploads, error, isLoading, refetch } = useQuery({
    queryKey: ['website-uploads'],
    queryFn: fetchWebsiteUploads,
  });

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const deleteMutation = useMutation({
    mutationFn: deleteWebsiteUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-uploads'] });
      setToast({ open: true, message: 'Upload deleted.', severity: 'success' });
      refetch();
    },
    onError: () => setToast({ open: true, message: 'Failed to delete upload.', severity: 'error' }),
  });

  const mutation = useMutation({
    mutationFn: createWebsiteUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-uploads'] });
      setOpen(false);
      setToast({ open: true, message: 'Upload added.', severity: 'success' });
      refetch();
    },
    onError: () => setToast({ open: true, message: 'Failed to add upload. Try again.', severity: 'error' }),
  });

  const formik = useFormik({
    initialValues: { title: '', caption: '', category: 'gallery', image: null },
    validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => formData.append(key, value));
      mutation.mutate(formData);
    },
  });

  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2';

  // Plain JSX, not a nested component — see TeamManagement: a nested
  // component remounts the inputs on every keystroke.
  const uploadForm = (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => setOpen(false)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 mb-4 transition-colors">
        <ArrowLeft size={16} />
        <span className="font-medium">Back to Uploads</span>
      </button>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
          <ImageIcon size={16} style={{ color: NAV }} />
          <div>
            <h3 className="text-sm font-bold text-slate-800">Add Website Upload</h3>
            <p className="text-xs text-slate-400">Images available to the corporate website (jamboricg-web) gallery.</p>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-6 space-y-5">
          <div>
            <label className={labelCls}>Title</label>
            <input name="title" value={formik.values.title} onChange={formik.handleChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Caption</label>
            <input name="caption" value={formik.values.caption} onChange={formik.handleChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select name="category" value={formik.values.category} onChange={formik.handleChange} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}>Image</label>
            <input
              ref={(ref) => { window.uploadFileInputRef = ref; }}
              type="file" accept="image/*" className="hidden"
              onChange={(e) => { const file = e.target.files[0]; if (file) formik.setFieldValue('image', file); }}
            />
            <div onClick={() => window.uploadFileInputRef?.click()}
              className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-teal-400 hover:bg-teal-50/40 transition-all duration-200 cursor-pointer">
              <UploadCloud size={28} className="mx-auto text-slate-400" />
              <div className="mt-3">
                <p className="text-sm font-medium text-slate-700">Click to upload</p>
                <p className="text-xs text-slate-400 mt-1">JPEG, PNG or GIF (Max 10MB)</p>
              </div>
            </div>
            {formik.touched.image && formik.errors.image && <p className="text-red-500 text-xs mt-2">{formik.errors.image}</p>}
            {formik.values.image && (
              <img src={URL.createObjectURL(formik.values.image)} alt="Preview" className="mt-4 w-full h-48 object-cover rounded-lg border border-slate-200" />
            )}
          </div>

          <button type="submit" disabled={mutation.isLoading}
            className="w-full bg-teal-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors disabled:opacity-50">
            {mutation.isLoading ? 'Uploading...' : 'Add Upload'}
          </button>
        </form>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
          <h3 className="text-sm font-bold text-red-800 mb-1">Error Loading Uploads</h3>
          <p className="text-sm text-red-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  const filtered = filter === 'all' ? uploads : uploads?.filter((u) => u.category === filter);

  return (
    <div className="p-6 space-y-6">
      {open ? uploadForm : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Website Uploads</h2>
              <p className="text-xs text-slate-400 mt-0.5">Manage images used across the corporate website — gallery, hero images, project photos.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                <RefreshCw size={13} /> Refresh
              </button>
              <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
                <Plus size={13} /> Add Upload
              </button>
            </div>
          </div>

          <div className="flex gap-1">
            {['all', ...CATEGORIES.map((c) => c.value)].map((cat) => (
              <button key={cat} onClick={() => setFilter(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                  ${filter === cat ? 'bg-teal-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {cat === 'all' ? 'All' : CATEGORIES.find((c) => c.value === cat)?.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered?.map((u) => (
                <div key={u.id} className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
                  <div className="relative">
                    <img src={`${IMG_BASE_URL}${u.image}`} alt={u.title || 'Upload'} className="w-full h-40 object-cover" />
                    <button onClick={() => deleteMutation.mutate(u.id)} disabled={deleteMutation.isLoading}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all duration-200 disabled:opacity-50">
                      <Trash2 size={14} />
                    </button>
                    <span className="absolute bottom-2 left-2 text-xs font-semibold bg-black/60 text-white rounded-full px-2 py-0.5">
                      {CATEGORIES.find((c) => c.value === u.category)?.label}
                    </span>
                  </div>
                  <div className="p-4">
                    {u.title && <p className="text-sm font-semibold text-slate-800 truncate">{u.title}</p>}
                    <p className="text-xs text-slate-400 font-medium">{new Date(u.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {filtered?.length === 0 && (
                <div className="col-span-full text-center py-10 text-slate-400">No uploads found</div>
              )}
            </div>
          )}
        </>
      )}
      <Toast toast={toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
    </div>
  );
}

export default WebsiteUploads;
